import { isMacOS } from '../utils/platform';
// Direct-download URLs for the Atlas Desktop installer, per platform.
// Relative so they resolve to whichever host (dev/stage/prod) served the page.
// In production these are published by desktop-release.yml to
// /var/www/html/downloads/ and served by nginx's existing `location /`.
export const DESKTOP_DOWNLOAD_URLS = {
  windows: '/downloads/atlas-setup.exe',
  macos: '/downloads/atlas-setup.dmg',
} as const;

export type DesktopPlatform = keyof typeof DESKTOP_DOWNLOAD_URLS;

/// Which installer to offer, and (in the desktop build) which title-bar layout
/// to use. Detection lives in utils/platform so this and the sidebar banner
/// gate can never disagree. Anything not identified as a Mac falls back to the
/// Windows build — the overwhelming majority of Atlas users, and the sensible
/// default behind the "need the other version?" link.
export function detectPlatform(): DesktopPlatform {
  return isMacOS() ? 'macos' : 'windows';
}

/// File name the browser saves the installer as, per platform.
export function desktopInstallerName(platform: DesktopPlatform = detectPlatform()): string {
  return platform === 'macos' ? 'atlas-setup.dmg' : 'atlas-setup.exe';
}

/// URL of the installer for the given (or detected) platform.
export function desktopDownloadUrl(platform: DesktopPlatform = detectPlatform()): string {
  return DESKTOP_DOWNLOAD_URLS[platform];
}

// Kept for callers that just want "the installer for this machine".
export const DESKTOP_DOWNLOAD_URL = desktopDownloadUrl();

// Programmatically trigger the installer download (used by the sidebar banner).
export function triggerDesktopDownload(platform: DesktopPlatform = detectPlatform()): void {
  const a = document.createElement('a');
  a.href = desktopDownloadUrl(platform);
  a.download = desktopInstallerName(platform);
  document.body.appendChild(a);
  a.click();
  a.remove();
}
